import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
    createMachineCenterCondition,
    createFunctionalLocationCondition,
    FAILURE_TYPES_EXCEPTIONS_WHERE,
} from "./helpers";
import { useRCALibraryList } from "./useRCALibraryList";
import {
    IFishboneDiagramCategory,
    IGetRCADetailsOptionsQueries,
    IGetRCADetailsOptionsReturn,
    IHistoryLogRCA,
    IRCA,
    IRCALibraryEntity,
    IRCALibraryTableOperationFilters,
} from "./RCALibraryEntityInterfaces";
import { IMill } from "pages/Osorno/OsornoSetupDashboard/Corporations/CorporationsAPIInterfaces";
import {
    getFailureCodeOptions,
    getFailureTypeOptions,
    getFishboneCategories,
    getFunctionalLocationOptions,
    getLastSevenDaysDowntimeLengthByType,
    getMachineCenterOptions,
    getMills,
    getProductionLineOptions,
    getRCA,
    getRCAHistoricChangesLog,
    getShiftOptions,
    insertObservationRCA,
    updateFiveWhyAnalysisMediaQuery,
    updateRCAMedia,
    upsertFishboneDiagram,
    upsertFishboneDiagramProblemCause,
    upsertRCA5WhyAnalysis,
    upsertRCA5WhyAnalysisItemQuery,
    upsertRCADetails,
    upsertRCAWithTeamMembers,
} from "api/queries/RCA/RCALibraryAPI";
import { IRCALibraryFilterSelected } from "pages/Corporate/RCA/RCALibrary/components/RCALibraryFilters/RCALibraryFiltersInterfaces";
import { DefaultTranslationFieldsInTable, MEASURE_METRICS, notSelected, RCA_STATUSES } from "constants/constants";
import { IMyRCAsFilters } from "pages/Corporate/RCA/RCADashboard/components/MyRCAs/MyRCAsInterfaces";
import { getEndOfDateRange, getStartOfDateRange } from "utilities/utilities";
import moment from "moment";
import { ICreateEditManualRCAModalFormValues } from "pages/Corporate/RCA/RCALibrary/components/RCALibraryDetail/components/CreateEditManualRCAModal/CreateEditManualRCAModalInterfaces";
import { IOption } from "components/NewMainSelect/NewMainSelectInterfaces";
import { useTranslation } from "react-i18next";
import { handleNameTranslation, translateDefaultEntity } from "utilities/localesUtilities";
import { IFailureCode, IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";

export const RCALibraryEntity: IRCALibraryEntity = {
    GetMills: () => {
        const { loading, error, data } = useQuery(getMills);
        const [mills, setMills] = useState<IMill[]>([]);

        useEffect(() => {
            if (data?.mill && Array.isArray(data.mill)) {
                setMills(data.mill);
            }
        }, [data]);

        return {
            loading,
            mills,
            apolloQueryError: error,
        };
    },
    GetRCALibrary: (tableOperationFilters: IRCALibraryTableOperationFilters, filters: IRCALibraryFilterSelected) => {
        const whereVariables = useMemo(
            () => ({
                status: {
                    status_id: {
                        ...(!filters.showCompleted
                            ? { _neq: RCA_STATUSES.COMPLETED }
                            : { _eq: RCA_STATUSES.COMPLETED }),
                    },
                },
                ...(filters?.meetingTypeIds &&
                    !filters.meetingTypeIds.includes(notSelected) &&
                    filters.totalMeetingTypes &&
                    filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                        meeting_type_id: { _in: filters.meetingTypeIds },
                    }),

                ...(filters?.meetingTypeIds &&
                    filters.meetingTypeIds.includes(notSelected) && {
                        meeting_type_id: { _is_null: true },
                    }),
                ...(filters.searchText && {
                    _or: [
                        { incident_description: { _ilike: `%${filters.searchText}%` } },
                        ...(Number.isInteger(parseInt(filters.searchText))
                            ? [{ sequence_id: { _eq: parseInt(filters.searchText) } }]
                            : []),
                        { owner: { first_name: { _ilike: `%${filters.searchText}%` } } },
                        { owner: { last_name: { _ilike: `%${filters.searchText}%` } } },
                    ],
                }),
                ...(filters.ownerId &&
                    filters.ownerId.length > 0 && {
                        owner_id: {
                            _in: filters.ownerId,
                        },
                    }),
                ...(filters.startDate &&
                    filters.endDate && {
                        due_date: {
                            _gte: filters.startDate,
                            _lte: filters.endDate,
                        },
                    }),
                mill_id: {
                    _in: filters.millsIds,
                },
                ...((filters.shiftId && filters.shiftId.length > 0) ||
                (filters.productionLineId && filters.productionLineId.length > 0) ||
                (filters.machineCenterId && filters.machineCenterId.length > 0) ||
                (filters.functionalLocationId && filters.functionalLocationId.length > 0) ||
                (filters.failureTypeId && filters.failureTypeId.length > 0)
                    ? {
                          _or: [
                              {
                                  rca_event_reports: {
                                      event_report: {
                                          ...(filters.shiftId &&
                                              filters.shiftId.length > 0 && {
                                                  shift_day: {
                                                      shift_historic: {
                                                          shift_id: {
                                                              _in: filters.shiftId,
                                                          },
                                                      },
                                                  },
                                              }),
                                          machine_center: {
                                              ...(filters.productionLineId &&
                                                  filters.productionLineId.length > 0 && {
                                                      production_line_id: {
                                                          _in: filters.productionLineId,
                                                      },
                                                  }),
                                              ...(filters.machineCenterId &&
                                                  filters.machineCenterId.length > 0 && {
                                                      machine_center_id: {
                                                          _in: filters.machineCenterId,
                                                      },
                                                  }),
                                          },
                                          ...(filters.functionalLocationId &&
                                              filters.functionalLocationId.length > 0 && {
                                                  functional_location: {
                                                      functional_location_id: {
                                                          _in: filters.functionalLocationId,
                                                      },
                                                  },
                                              }),
                                          ...(filters.failureTypeId &&
                                              filters.failureTypeId.length > 0 && {
                                                  failure_type_id: {
                                                      _in: filters.failureTypeId,
                                                  },
                                              }),
                                      },
                                  },
                              },
                              {
                                  rca_detail: {
                                      ...(filters.shiftId &&
                                          filters.shiftId.length > 0 && {
                                              shift_id: {
                                                  _in: filters.shiftId,
                                              },
                                          }),
                                      ...(filters.productionLineId &&
                                          filters.productionLineId.length > 0 && {
                                              production_line_id: {
                                                  _in: filters.productionLineId,
                                              },
                                          }),
                                      ...(filters.machineCenterId &&
                                          filters.machineCenterId.length > 0 && {
                                              machine_center_id: {
                                                  _in: filters.machineCenterId,
                                              },
                                          }),
                                      ...(filters.functionalLocationId &&
                                          filters.functionalLocationId.length > 0 && {
                                              functional_location_id: {
                                                  _in: filters.functionalLocationId,
                                              },
                                          }),
                                      ...(filters.failureTypeId &&
                                          filters.failureTypeId.length > 0 && {
                                              failure_type_id: {
                                                  _in: filters.failureTypeId,
                                              },
                                          }),
                                  },
                              },
                          ],
                      }
                    : {}),
                ...(filters.keySuccessFactorId &&
                    filters.keySuccessFactorId.length > 0 && {
                        key_success_factor_id: {
                            _in: filters.keySuccessFactorId,
                        },
                    }),
            }),
            [filters],
        );

        const {
            items,
            totalCount,
            loading,
            apolloQueryError: error,
            refetch,
        } = useRCALibraryList(whereVariables, tableOperationFilters);

        return {
            rcaLibrary: items,
            refetch,
            loading,
            apolloQueryError: error,
            totalCount,
        };
    },
    GetUserRCAs: (tableOperationFilters: IRCALibraryTableOperationFilters, userId, filters: IMyRCAsFilters) => {
        const whereVariables = useMemo(
            () => ({
                owner_id: {
                    _eq: userId,
                },
                status: {
                    status_id: {
                        ...(!filters.showCompleted
                            ? { _neq: RCA_STATUSES.COMPLETED }
                            : { _eq: RCA_STATUSES.COMPLETED }),
                    },
                },
            }),
            [userId, filters],
        );

        const {
            items,
            totalCount,
            loading,
            apolloQueryError: error,
        } = useRCALibraryList(whereVariables, tableOperationFilters);

        return {
            userRCAs: items,
            loading,
            apolloQueryError: error,
            totalCount,
        };
    },
    GetRCA: (rcaId: number) => {
        const { data, error, loading, refetch } = useQuery(getRCA, {
            fetchPolicy: "no-cache",
            variables: { rca_id: rcaId },
        });

        const [rca, setRCA] = useState<IRCA>({} as IRCA);

        useEffect(() => {
            if (data?.rca && Array.isArray(data.rca) && data.rca[0]) {
                setRCA(data.rca[0]);
            }
        }, [data]);

        return { apolloQueryError: error, rca, loading, refetch };
    },
    GetRCAHistoricChangesLog: (id?: number) => {
        const [rcaLogs, setRcaLogs] = useState<IHistoryLogRCA[]>([]);

        const { error, loading, refetch } = useQuery(getRCAHistoricChangesLog, {
            skip: !id,
            onCompleted: (data) => {
                if (data?.rca_history_log && Array.isArray(data.rca_history_log)) {
                    setRcaLogs(data.rca_history_log);
                }
            },
            variables: {
                id: id,
            },
        });

        return { apolloQueryError: error, rcaLogs, loading, refetch };
    },
    GetFishboneCategories() {
        const { loading, error, data } = useQuery(getFishboneCategories);
        const [fishboneCategories, setFishboneCategories] = useState<IFishboneDiagramCategory[]>([]);

        useEffect(() => {
            if (data?.fishbone_diagram_category && Array.isArray(data.fishbone_diagram_category)) {
                setFishboneCategories(data.fishbone_diagram_category);
            }
        }, [data]);

        return {
            fishboneCategories,
            loading,
            apolloQueryError: error,
        };
    },
    GetLastSevenDaysDowntimeLengthByType(failureTypeId?: number, machineCenterId?: number, endDate?: Date) {
        const [lastSevenDaysDowntime, setLastSevenDaysDowntime] = useState<number | undefined>(undefined);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        const { loading, error, refetch } = useQuery(getLastSevenDaysDowntimeLengthByType, {
            skip: !failureTypeId || !machineCenterId || !endDate,
            variables: {
                where: {
                    ...(failureTypeId && {
                        failure_type_id: {
                            _eq: failureTypeId,
                        },
                    }),
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                    start_date: {
                        _gte: getStartOfDateRange(moment(endDate).subtract(7, "days").toDate()),
                        _lte: getEndOfDateRange(endDate || new Date()),
                    },
                    machine_center_id: { _eq: machineCenterId },
                    ignore_event: { _eq: false },
                },
            },
            onCompleted: (data) => {
                if (data?.event_report && Array.isArray(data.event_report)) {
                    const eventSummary: { event_report_id: number; valid_time: number }[] = data.event_report;
                    const totalSeconds = eventSummary.reduce(
                        (total: number, event: { event_report_id: number; valid_time: number }) =>
                            total + event.valid_time,
                        0,
                    );
                    const totalMinutes = Math.round(totalSeconds / 60) || 0;
                    setLastSevenDaysDowntime(totalMinutes);
                }
            },
        });

        useEffect(() => {
            if (error) {
                setApolloQueryError(error);
            }
        }, [error]);

        return { loading, lastSevenDaysDowntime, apolloQueryError, refetch };
    },
    GetRCADetailsOptions(optionsSelected: ICreateEditManualRCAModalFormValues) {
        const { t, i18n } = useTranslation();

        const [productionLineOptions, setProductionLineOptions] = useState<
            IGetRCADetailsOptionsReturn["productionLineOptions"]
        >([]);
        const [machineCenterOptions, setMachineCenterOptions] = useState<
            IGetRCADetailsOptionsReturn["machineCenterOptions"]
        >([]);
        const [functionalLocationOptions, setFunctionalLocationOptions] = useState<
            IGetRCADetailsOptionsReturn["functionalLocationOptions"]
        >([]);
        const [failureTypeOptions, setFailureTypeOptions] = useState<IGetRCADetailsOptionsReturn["failureTypeOptions"]>(
            [],
        );
        const [failureCodeOptions, setFailureCodeOptions] = useState<IGetRCADetailsOptionsReturn["failureCodeOptions"]>(
            [],
        );
        const [secondaryFailureCodeOptions, setSecondaryFailureCodeOptions] = useState<
            IGetRCADetailsOptionsReturn["failureCodeOptions"]
        >([]);
        const [shiftOptions, setShiftOptions] = useState<IGetRCADetailsOptionsReturn["shiftOptions"]>([]);

        const [apolloQueryError, setApolloQueryError] = useState<string>("");

        const { loading: loadingProductionLines, error: errorProductionLines } = useQuery(getProductionLineOptions, {
            skip: optionsSelected?.isAdmin ? !optionsSelected?.millId : false,
            variables: {
                ...(optionsSelected?.millId && optionsSelected?.isAdmin
                    ? {
                          where: {
                              mill_id: { _eq: optionsSelected.millId },
                              deleted_at: { _is_null: true },
                          },
                      }
                    : {}),
            },
            onCompleted: (data) => {
                if (data?.production_line && Array.isArray(data.production_line)) {
                    const productionLines: IGetRCADetailsOptionsQueries["productionLineOptions"] = data.production_line;
                    const optionsFormatted: IOption[] = productionLines.map((pl) => ({
                        id: pl.production_line_id,
                        name: pl.name,
                    }));
                    setProductionLineOptions(optionsFormatted);
                }
            },
        });

        const { loading: loadingMachineCenters, error: errorMachineCenters } = useQuery(getMachineCenterOptions, {
            skip: !optionsSelected?.productionLineId,
            variables: {
                where: {
                    production_line_id: { _eq: optionsSelected.productionLineId },
                    deleted_at: { _is_null: true },
                    disabled_at: { _is_null: true },
                },
            },
            onCompleted: (data) => {
                if (data?.machine_center && Array.isArray(data.machine_center)) {
                    const machineCenters: IGetRCADetailsOptionsQueries["machineCenterOptions"] = data.machine_center;
                    const optionsFormatted: IOption[] = machineCenters.map((mc) => ({
                        id: mc.machine_center_id,
                        name: mc.name,
                    }));
                    setMachineCenterOptions(optionsFormatted);
                }
            },
        });

        const { loading: loadingFunctionalLocations, error: errorFunctionalLocations } = useQuery(
            getFunctionalLocationOptions,
            {
                skip: !optionsSelected?.machineCenterId,
                variables: {
                    where: {
                        area: {
                            machine_center_id: { _eq: optionsSelected?.machineCenterId },
                            disabled_at: { _is_null: true },
                        },
                        subordinate_to_id: { _is_null: true },
                        deleted_at: { _is_null: true },
                        disabled_at: { _is_null: true },
                    },
                },
                onCompleted: (data) => {
                    if (data?.functional_location && Array.isArray(data.functional_location)) {
                        const functionalLocations: IGetRCADetailsOptionsQueries["functionalLocationOptions"] =
                            data.functional_location;
                        const optionsFormatted: IOption[] = functionalLocations.map((fl) => ({
                            id: fl.functional_location_id,
                            name: handleNameTranslation(i18n.language, fl.translation.translation_names),
                            tag: handleNameTranslation(i18n.language, fl.area.translation.translation_names),
                        }));
                        setFunctionalLocationOptions(optionsFormatted);
                    }
                },
            },
        );

        const machineCenterCondition = createMachineCenterCondition(optionsSelected);
        const functionalLocationCondition = createFunctionalLocationCondition(optionsSelected);

        const { loading: loadingFailureTypes, error: errorFailureTypes } = useQuery(getFailureTypeOptions, {
            variables: {
                ...(optionsSelected?.machineCenterId ||
                optionsSelected?.functionalLocationId ||
                optionsSelected?.productionLineId
                    ? {
                          where: {
                              _and: [
                                  {
                                      _or: [
                                          { failure_codes: machineCenterCondition },
                                          { failure_codes: functionalLocationCondition },
                                          { corporate_id: { _is_null: true } },
                                      ],
                                  },
                                  FAILURE_TYPES_EXCEPTIONS_WHERE,
                              ],
                          },
                      }
                    : {
                          where: FAILURE_TYPES_EXCEPTIONS_WHERE,
                      }),
            },
            onCompleted: (data) => {
                if (data?.failure_type && Array.isArray(data.failure_type)) {
                    const failureTypes: IGetRCADetailsOptionsQueries["failureTypeOptions"] = data.failure_type;
                    const optionsFormatted: IOption[] = failureTypes.map((ft) => ({
                        id: ft.failure_type_id,
                        name: translateDefaultEntity(
                            i18n.language,
                            ft as unknown as IFailureType,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_TYPE,
                        ),
                    }));
                    setFailureTypeOptions(optionsFormatted);
                }
            },
        });

        const { loading: loadingFailureCodes, error: errorFailureCodes } = useQuery(getFailureCodeOptions, {
            skip: !optionsSelected?.failureTypeId,
            variables: {
                where: {
                    ...(optionsSelected?.machineCenterId ||
                    optionsSelected?.functionalLocationId ||
                    optionsSelected?.productionLineId
                        ? {
                              _or: [
                                  machineCenterCondition,
                                  functionalLocationCondition,
                                  {
                                      _and: [
                                          { corporate_id: { _is_null: true } },
                                          {
                                              failure_type: {
                                                  is_location_specific: { _eq: false },
                                                  corporate_id: { _is_null: true },
                                              },
                                          },
                                      ],
                                  },
                              ],
                          }
                        : {}),
                    failure_type_id: { _eq: optionsSelected?.failureTypeId },
                    primary_code_id: { _is_null: true },
                    deleted_at: { _is_null: true },
                    failure_type: { deleted_at: { _is_null: true } },
                },
            },
            onCompleted: (data) => {
                if (data?.failure_code && Array.isArray(data.failure_code)) {
                    const failureCodes: IGetRCADetailsOptionsQueries["failureCodeOptions"] = data.failure_code;
                    const optionsFormatted: IOption[] = failureCodes.map((fc) => ({
                        id: fc.failure_code_id,
                        name: translateDefaultEntity(
                            i18n.language,
                            fc as unknown as IFailureCode,
                            "name",
                            DefaultTranslationFieldsInTable.FAILURE_CODE,
                        ),
                    }));
                    setFailureCodeOptions(optionsFormatted);
                }
            },
        });

        const { loading: loadingSecondaryFailureCodes, error: errorSecondaryFailureCodes } = useQuery(
            getFailureCodeOptions,
            {
                skip: !optionsSelected?.failureCodeId,
                variables: {
                    where: {
                        ...(optionsSelected?.machineCenterId ||
                        optionsSelected?.functionalLocationId ||
                        optionsSelected?.productionLineId
                            ? {
                                  _or: [machineCenterCondition, functionalLocationCondition],
                              }
                            : {}),
                        primary_code_id: { _eq: optionsSelected?.failureCodeId },
                        deleted_at: { _is_null: true },
                        failure_type: { deleted_at: { _is_null: true } },
                    },
                },
                onCompleted: (data) => {
                    if (data?.failure_code && Array.isArray(data.failure_code)) {
                        const secondaryFailureCodes: IGetRCADetailsOptionsQueries["failureCodeOptions"] =
                            data.failure_code;
                        const optionsFormatted: IOption[] = secondaryFailureCodes.map((fc) => ({
                            id: fc.failure_code_id,
                            name: translateDefaultEntity(
                                i18n.language,
                                fc as unknown as IFailureCode,
                                "name",
                                DefaultTranslationFieldsInTable.FAILURE_CODE,
                            ),
                        }));
                        setSecondaryFailureCodeOptions(optionsFormatted);
                    }
                },
            },
        );

        const { loading: loadingShifts, error: errorShifts } = useQuery(getShiftOptions, {
            variables: {
                where: {
                    deleted_at: { _is_null: true },
                    disabled_at: { _is_null: true },
                    production_line_shifts: { production_line_shift_id: { _gt: 0 }, disabled_at: { _is_null: true } },
                    ...(optionsSelected.productionLineId
                        ? {
                              production_line_shifts: {
                                  production_line_id: { _eq: optionsSelected.productionLineId },
                                  disabled_at: { _is_null: true },
                              },
                          }
                        : {}),
                    ...(optionsSelected.millId
                        ? {
                              production_line_shifts: {
                                  production_line: { mill_id: { _eq: optionsSelected.millId } },
                                  disabled_at: { _is_null: true },
                              },
                          }
                        : {}),
                },
            },
            onCompleted: (data) => {
                if (data?.shift && Array.isArray(data.shift)) {
                    const shifts: IGetRCADetailsOptionsQueries["shiftOptions"] = data.shift;
                    const optionsFormatted: IOption[] = shifts.map((s) => ({
                        id: s.shift_id,
                        name: s.name,
                    }));
                    setShiftOptions(optionsFormatted);
                }
            },
        });

        useEffect(() => {
            if (
                errorProductionLines ||
                errorMachineCenters ||
                errorFunctionalLocations ||
                errorFailureTypes ||
                errorFailureCodes ||
                errorShifts ||
                errorSecondaryFailureCodes
            ) {
                setApolloQueryError(t("rca.rca_library.rca_detail.create_rca_modal.rca_details_options_error"));
            }
        }, [
            errorProductionLines,
            errorMachineCenters,
            errorFunctionalLocations,
            errorFailureTypes,
            errorFailureCodes,
            errorShifts,
            errorSecondaryFailureCodes,
            t,
        ]);

        return {
            productionLineOptions,
            machineCenterOptions,
            functionalLocationOptions,
            failureTypeOptions,
            failureCodeOptions,
            shiftOptions,
            secondaryFailureCodeOptions,
            loadingProductionLines,
            loadingMachineCenters,
            loadingFunctionalLocations,
            loadingShifts,
            loadingFailureTypes,
            loadingFailureCodes,
            loadingSecondaryFailureCodes,
            apolloQueryError,
        };
    },
    UpsertRCADetails: () => {
        const [upsertRCADetailsMutation, { error, loading }] = useMutation(upsertRCADetails);
        return { apolloMutationError: error, upsertRCADetailsMutation, loading };
    },
    UpsertRCAWithTeamMembers: () => {
        const [upsertSetRCAWithTeamMembers, { error, loading }] = useMutation(upsertRCAWithTeamMembers);
        return { apolloMutationError: error, upsertSetRCAWithTeamMembers, loading };
    },
    UpsertRCA5WhyAnalysis: () => {
        const [upsertSetRCA5WhyAnalysis, { error, loading }] = useMutation(upsertRCA5WhyAnalysis);
        return { apolloMutationError: error, upsertSetRCA5WhyAnalysis, loading };
    },
    UpsertRCA5WhyAnalysisItem: () => {
        const [upsertRCA5WhyAnalysisItem, { error, loading }] = useMutation(upsertRCA5WhyAnalysisItemQuery);
        return { apolloMutationError: error, upsertRCA5WhyAnalysisItem, loading };
    },
    UpdateFiveWhyAnalysisMedia: () => {
        const [updateFiveWhyAnalysisMedia, { error, loading }] = useMutation(updateFiveWhyAnalysisMediaQuery);
        return { apolloMutationError: error, updateFiveWhyAnalysisMedia, loading };
    },
    UpsertFishboneDiagram: () => {
        const [upsertFishboneDiagramItem, { error, loading }] = useMutation(upsertFishboneDiagram);
        return { apolloMutationError: error, upsertFishboneDiagramItem, loading };
    },
    UpsertFishboneDiagramProblemCause: () => {
        const [upsertFishboneDiagramProblemCauseItem, { error, loading }] = useMutation(
            upsertFishboneDiagramProblemCause,
        );
        return { apolloMutationError: error, upsertFishboneDiagramProblemCauseItem, loading };
    },
    InsertObservationRCA: () => {
        const [createObservationRCA, { error }] = useMutation(insertObservationRCA);
        return { apolloMutationError: error, createObservationRCA };
    },
    UpdateRCAMedia: () => {
        const [updateRCAMediaMutation, { error }] = useMutation(updateRCAMedia);
        return { apolloMutationError: error, updateRCAMediaMutation };
    },
};
